/// Marine diesel deep-dive — the components, the systems, and the
/// troubleshooting tree that every Coastal Skipper + Yachtmaster
/// candidate should be able to walk through. Extends the procedure-
/// based content in `code.ts` (WOBBLE + scheduled maintenance) with
/// the conceptual material that supports it.
///
/// Three domains:
///
///   1. Engine systems — the four working systems of a marine diesel
///      (fuel, air, cooling, electrical / lubrication).
///   2. Components — the parts that make up each system.
///   3. Troubleshooting tree — symptom → likely cause → action.

// ───────────────────────────────────────────────────────────────────
// 1. Engine systems
// ───────────────────────────────────────────────────────────────────

export type EngineSystemId = "fuel" | "air" | "cooling" | "lubrication" | "electrical" | "exhaust";

export interface EngineSystem {
  id: EngineSystemId;
  name: string;
  /// One-line gist.
  summary: string;
  /// What the system does in the engine cycle.
  purpose: string;
  /// Main components that make it up.
  components: readonly string[];
  /// Most common failure mode of the system.
  failureModeMostCommon: string;
}

export const ENGINE_SYSTEMS: readonly EngineSystem[] = Object.freeze([
  {
    id: "fuel",
    name: "Fuel system",
    summary:
      "Delivers clean diesel from the tank to the injectors at the right pressure + timing.",
    purpose:
      "Stores fuel, filters water + particulates out, raises it to injection pressure, and delivers it as a fine mist to the combustion chambers at the right moment in the cycle.",
    components: [
      "Fuel tank + filler + breather",
      "Primary filter / water separator (Racor or equivalent) with drain valve",
      "Lift pump (mechanical or electric)",
      "Secondary filter (engine-mounted)",
      "Injection pump (high-pressure pump driven by the camshaft)",
      "Injectors (one per cylinder)",
      "Fuel return line",
      "Stop solenoid (electric) or mechanical stop lever",
    ],
    failureModeMostCommon:
      "Contaminated fuel — water + dirt + biological growth in the tank, blocking filters + starving injectors. Run regular fuel-polishing cycles or add biocide.",
  },
  {
    id: "air",
    name: "Air intake system",
    summary:
      "Delivers clean, cool air to the cylinders for combustion. The 'breathing' side of the engine.",
    purpose:
      "Filters intake air for particulates + salt; on turbocharged engines, the turbo compresses + cools the air to boost combustion mass.",
    components: [
      "Air filter (foam or paper)",
      "Inlet manifold",
      "Turbocharger (on turbo engines)",
      "Intercooler / aftercooler (cools compressed turbo air)",
      "Inlet valves",
    ],
    failureModeMostCommon:
      "Blocked air filter — engine runs rich + smokes black. Check + replace annually OR every 200 hours.",
  },
  {
    id: "cooling",
    name: "Cooling system",
    summary:
      "Removes heat from the engine — keeps the block + head at design temperature.",
    purpose:
      "Marine diesels use INDIRECT cooling: a fresh-water (coolant) loop circulates inside the engine; raw sea water cools the coolant via a heat exchanger before being expelled out the exhaust.",
    components: [
      "Raw-water sea-cock + strainer",
      "Raw-water pump (impeller pump)",
      "Heat exchanger (tube-and-shell or plate)",
      "Coolant header tank (radiator equivalent)",
      "Coolant pump (impeller or centrifugal)",
      "Thermostat (opens at ~75-80°C to circulate coolant)",
      "Exhaust elbow (raw water injected into exhaust to cool it)",
    ],
    failureModeMostCommon:
      "Raw-water pump impeller failure — no cooling water flow + engine overheats in minutes. Spare impeller + cover gasket on every boat.",
  },
  {
    id: "lubrication",
    name: "Lubrication system",
    summary:
      "Circulates oil under pressure to bearings, cam followers + piston rings.",
    purpose:
      "Pressurised oil reduces friction in bearings, removes heat from pistons + bearings, and keeps moving parts separated by an oil film.",
    components: [
      "Sump (oil reservoir)",
      "Oil pump (driven by the camshaft)",
      "Oil filter",
      "Oil cooler (heat exchanger off the raw-water side, on larger engines)",
      "Oil galleries (drilled passages in the block delivering oil)",
      "Dipstick + filler cap",
    ],
    failureModeMostCommon:
      "Low oil level OR contaminated oil — both reduce lubrication + accelerate wear. Check daily (WOBBLE); change every 200 hrs / 12 months.",
  },
  {
    id: "electrical",
    name: "Electrical system",
    summary:
      "Starts the engine, generates power for the boat, and runs ancillary loads (instruments, alarms).",
    purpose:
      "Starter motor cranks the engine; alternator generates charge while the engine runs; control circuits manage start / stop / alarms.",
    components: [
      "Start battery (cranking)",
      "House battery (deep-cycle)",
      "Starter motor (high-current DC motor)",
      "Alternator (engine-driven, generates AC then rectifies to DC)",
      "Voltage regulator",
      "Battery isolator + main switch",
      "Engine control panel with alarms (low oil pressure, high water temp, no charge)",
    ],
    failureModeMostCommon:
      "Flat or worn-out battery — engine won't crank. Combined with corroded terminals or wrong isolator setting. Check terminals annually + replace batteries at 5-7 years.",
  },
  {
    id: "exhaust",
    name: "Exhaust system",
    summary:
      "Removes spent combustion gases from the cylinders + the boat.",
    purpose:
      "Carries hot exhaust gas + cooling raw water from the exhaust elbow, through a hose, to the silencer / muffler, then to the through-hull exhaust outlet.",
    components: [
      "Exhaust elbow (raw water injected here)",
      "Wet exhaust hose (heat- + chemical-resistant)",
      "Anti-siphon valve (prevents water back-flow into the engine)",
      "Silencer / muffler",
      "Through-hull exhaust skin fitting",
    ],
    failureModeMostCommon:
      "Corroded exhaust elbow — at the saltwater injection point. Annual inspection + replacement every 3-5 years.",
  },
]);

export const ENGINE_SYSTEM_IDS: readonly EngineSystemId[] = ENGINE_SYSTEMS.map(
  (s) => s.id,
);

// ───────────────────────────────────────────────────────────────────
// 2. Troubleshooting tree — symptom → diagnosis → action
// ───────────────────────────────────────────────────────────────────

export interface TroubleshootingNode {
  id: string;
  /// Top-level symptom the skipper observes.
  symptom: string;
  /// Steps to diagnose, in priority order — quick + cheap first.
  diagnose: readonly string[];
  /// Most-likely causes, ranked.
  likelyCauses: readonly string[];
  /// Action to take once the cause is identified.
  action: string;
}

export const TROUBLESHOOTING_TREE: readonly TroubleshootingNode[] = Object.freeze([
  {
    id: "wont-start",
    symptom: "Engine won't start (no crank or weak crank)",
    diagnose: [
      "Check the battery isolator switch — is it ON?",
      "Listen during crank attempt — is the starter motor turning at all?",
      "Check voltmeter — does the voltage drop when cranking?",
      "Check battery terminals for corrosion or looseness.",
      "Check the starter solenoid — does it click when you turn the key?",
    ],
    likelyCauses: [
      "Battery flat / disconnected / isolator off",
      "Corroded terminals — high resistance, voltage drop",
      "Failed starter motor solenoid (clicks but no crank)",
      "Failed starter motor (no click)",
      "Loose wiring at the starter circuit",
    ],
    action:
      "Recharge battery OR check + tighten terminals OR replace starter solenoid / motor. For an immediate sail, can be cranked manually with a hand starter if fitted.",
  },
  {
    id: "starts-then-stops",
    symptom: "Engine starts but stops after a few seconds",
    diagnose: [
      "Smell for fuel in the engine bay — is there a fuel leak?",
      "Check the stop solenoid — is it pulled in (some engines auto-release after start)?",
      "Check the fuel filter bowl — is there water?",
      "Check fuel level + tank pickup pipe.",
    ],
    likelyCauses: [
      "Air in the fuel system (after fuel starvation or filter change)",
      "Water-contaminated fuel reaching the injection pump",
      "Stop solenoid mis-wired or stuck",
      "Fuel tank pickup pipe clogged or above the remaining fuel level",
    ],
    action:
      "Bleed the fuel system (see diesel-bleed procedure). Drain water from the Racor filter bowl. Replace primary filter if heavily contaminated.",
  },
  {
    id: "white-smoke",
    symptom: "White or grey smoke at start-up (cold engine)",
    diagnose: [
      "Is the smoke clearing after the engine warms up?",
      "What's the engine temperature?",
      "Check coolant level — is it dropping?",
    ],
    likelyCauses: [
      "Cold-start condensation (normal in cold weather; clears in minutes)",
      "Water in the cylinders (head gasket leak OR cracked head)",
      "Worn glow plugs — not pre-heating cylinders before start",
    ],
    action:
      "If clears in 1-2 min: normal cold-start, no action. If persists: stop the engine, check coolant level + oil for water contamination (creamy oil = head gasket failure). Major fault: requires a mechanic.",
  },
  {
    id: "black-smoke",
    symptom: "Black smoke at speed (especially on acceleration)",
    diagnose: [
      "Check the air filter — is it blocked?",
      "Check the boat's load — overloaded or marine-fouled hull?",
      "Check engine timing + injector spray pattern (specialist).",
    ],
    likelyCauses: [
      "Air filter blocked — rich fuel mixture",
      "Overloaded boat / heavily fouled hull — engine working too hard",
      "Worn injectors — poor spray, incomplete combustion",
      "Injection timing maladjusted",
    ],
    action:
      "Clean / replace air filter; clean hull bottom; if injectors are worn, have them serviced by a specialist. Black smoke is wasted fuel + engine wear.",
  },
  {
    id: "blue-smoke",
    symptom: "Blue smoke at low load (especially when warm)",
    diagnose: [
      "Check oil level — is it dropping?",
      "Check the breather pipe for oil mist.",
      "Inspect dipstick for oil + water mix (creamy).",
    ],
    likelyCauses: [
      "Oil burning in the cylinders — worn piston rings + cylinder walls",
      "Worn valve stem seals",
      "Turbocharger seal failure (turbo engines)",
    ],
    action:
      "Major engine work — head gasket / piston rings rebuild. If turbo: have it serviced or replaced. NOT a temporary repair.",
  },
  {
    id: "overheating",
    symptom: "Engine overheating (high temperature alarm)",
    diagnose: [
      "Check raw-water exhaust outlet — is water flowing?",
      "Check raw-water strainer + sea-cock.",
      "Check coolant level in the header tank.",
      "Check belt tension on the raw-water + coolant pumps.",
      "Touch (carefully) the heat exchanger — is one end cold?",
    ],
    likelyCauses: [
      "Raw-water pump impeller failed (no water flow)",
      "Strainer blocked with weed / plastic",
      "Sea-cock partially closed",
      "Heat exchanger fouled with marine growth",
      "Coolant level too low",
      "Slipping belt — coolant pump not driven hard enough",
      "Thermostat stuck closed",
    ],
    action:
      "STOP THE ENGINE if you can — overheating destroys head gaskets fast. Identify cause + fix: clean strainer / open seacock / change impeller / top up coolant / tension belt / replace thermostat.",
  },
  {
    id: "loss-of-power",
    symptom: "Loss of power under load (engine bogs down)",
    diagnose: [
      "Check the propeller — fouled, damaged, or wrapped with line?",
      "Check the fuel filter — heavy load + fuel restriction?",
      "Check the air filter.",
      "Check engine temperature (overheating retards power).",
    ],
    likelyCauses: [
      "Propeller fouled or damaged",
      "Fuel filter restricted",
      "Air filter blocked",
      "Engine overheating + auto-derating",
      "Injection issue (specialist)",
    ],
    action:
      "Inspect prop (may need to dive); replace fuel + air filters; address overheating cause. Specialist required for injection issues.",
  },
  {
    id: "no-charge",
    symptom: "Battery not charging (voltmeter < 13.5 V engine running)",
    diagnose: [
      "Check belt tension on the alternator.",
      "Touch (carefully) the alternator — is it warm? (warm = working but maybe under-rated)",
      "Check voltage at the alternator output terminal.",
      "Check the smart-charger / regulator.",
    ],
    likelyCauses: [
      "Slipping alternator belt",
      "Failed alternator diodes (output stops or drops)",
      "Wrongly-set or failed regulator",
      "Disconnected wire to the battery",
      "Battery isolator switch on the wrong setting (1/2/Both/Off)",
    ],
    action:
      "Tension belt; check isolator setting; if alternator failed, replace OR have it rebuilt. NEVER turn isolator to OFF with engine running — kills alternator diodes.",
  },
]);

export const TROUBLESHOOTING_IDS: readonly string[] =
  TROUBLESHOOTING_TREE.map((n) => n.id);
